import React from 'react';
import Button from '@/components/UI/Button';
import DeleteButton from '@/components/UI/DeleteButton';
import EditButton from '@/components/UI/EditButton';
import EmptyState from '@/components/UI/EmptyState';
import type { GovernanceEntityTypeScope } from '@einhaus/project314-types';
import { ENTITY_TYPE_SCOPE_LABELS, type GovernanceEntityLimitDraft } from './governanceHelpers';

interface GovernanceEntityLimitsTableProps {
    entityLimits: GovernanceEntityLimitDraft[];
    onAdd: () => void;
    onEdit: (scope: GovernanceEntityTypeScope) => void;
    onDelete: (scope: GovernanceEntityTypeScope) => void;
}

const formatCap = (value: number | null): string => (value === null ? 'No cap' : value.toString());

/** The editable per-entity-type limit rows of a governance policy draft, rendered as a compact table. */
const GovernanceEntityLimitsTable: React.FC<GovernanceEntityLimitsTableProps> = ({ entityLimits, onAdd, onEdit, onDelete }) => (
    <div className="border-t border-zinc-800 pt-4">
        <div className="mb-2 flex items-center justify-between">
            <div>
                <h6 className="text-base font-semibold">Per-entity-type limits</h6>
                <p className="text-sm text-zinc-500">Hard caps applied per entity. Specific types override the “All” rule.</p>
            </div>
            <Button disabled={entityLimits.length >= 4} variant="link" onClick={onAdd}>
                + Add
            </Button>
        </div>

        {entityLimits.length === 0 ? (
            <EmptyState size="sm" title="No per-entity-type limits configured." />
        ) : (
            <div className="overflow-x-auto">
                <table className="min-w-full border-collapse bg-zinc-800 text-white">
                    <thead>
                        <tr className="bg-zinc-700 text-left">
                            <th className="px-3 py-2">Applies to</th>
                            <th className="px-3 py-2">Max positions</th>
                            <th className="px-3 py-2">Max same / opp</th>
                            <th className="px-3 py-2">Max risk %</th>
                            <th className="px-3 py-2">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {entityLimits.map((limit, index) => (
                            <tr className={index % 2 === 0 ? 'bg-zinc-800' : 'bg-zinc-750'} key={limit.entityTypeScope}>
                                <td className="border-t border-zinc-700 px-3 py-2">{ENTITY_TYPE_SCOPE_LABELS[limit.entityTypeScope]}</td>
                                <td className="border-t border-zinc-700 px-3 py-2">{formatCap(limit.maxPositionsPerEntity)}</td>
                                <td className="border-t border-zinc-700 px-3 py-2">
                                    {formatCap(limit.maxSameDirectionTradesPerEntity)} /{' '}
                                    {formatCap(limit.maxOppositeDirectionTradesPerEntity)}
                                </td>
                                <td className="border-t border-zinc-700 px-3 py-2">
                                    {limit.maxEntityRiskCapitalPercent === null ? 'No cap' : `${limit.maxEntityRiskCapitalPercent}%`}
                                </td>
                                <td className="border-t border-zinc-700 px-3 py-2">
                                    <div className="flex items-center gap-2">
                                        <EditButton size="sm" onClick={() => onEdit(limit.entityTypeScope)} />
                                        <DeleteButton size="sm" onClick={() => onDelete(limit.entityTypeScope)} />
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )}
    </div>
);

export default GovernanceEntityLimitsTable;
